class NumberElementFactory extends ElementFactory {
  static config = {
    type: 'text',
    inputMode: 'decimal',
    step: 1,
    min: null,
    max: null,
    precision: 0,                // Number of decimal places
    padToPrecision: false,       // Pad with zeros to match precision
    useGrouping: false,          // Use thousand separators
    groupingSeparator: ',',      // Thousand separator character
    decimalSeparator: '.',       // Decimal point character
    allowNegative: false,        // Allow negative values
    roundValues: false,          // Round to precision
    showSymbol: false,           // Show currency/unit symbol
    symbol: '',                  // Symbol to display (e.g., '$')
    symbolPosition: 'before',    // Symbol position ('before' or 'after')
    negativeWithParentheses: false, // Use parentheses for negative values
    validationMessages: {
      required: 'Please fill in this field',
      min: 'Value must be at least {min}',
      max: 'Value must be no more than {max}',
      step: 'Value must be a multiple of {step}',
      number: 'Please enter a valid number',
      decimal: 'Only one decimal separator is allowed',
      negative: 'Negative sign must be at the start'
    }
  };

  static propertyHandlers = {
    min: {
      get(element) {
        const min = element.getAttribute('min');
        return min !== null ? parseFloat(min) : null;
      },
      set(instance, newValue) {
        if (newValue === null || newValue === undefined) {
          instance.element.removeAttribute('min');
        } else {
          instance.element.setAttribute('min', newValue);
        }
      }
    },
    max: {
      get(element) {
        const max = element.getAttribute('max');
        return max !== null ? parseFloat(max) : null;
      },
      set(instance, newValue) {
        if (newValue === null || newValue === undefined) {
          instance.element.removeAttribute('max');
        } else {
          instance.element.setAttribute('max', newValue);
        }
      }
    },
    step: {
      get(element) {
        const step = element.getAttribute('step');
        return step !== null ? parseFloat(step) : 1;
      },
      set(instance, newValue) {
        instance.element.setAttribute('step', newValue);
      }
    },
    precision: {
      get(element) {
        const precision = element.getAttribute('precision');
        return precision !== null ? parseFloat(precision) : 0;
      },
      set(instance, newValue) {
        const oldValue = this.precision;
        if (oldValue !== newValue) {
          instance.element.setAttribute('precision', newValue);
          instance.applyFormatting(instance);
        }
      }
    }
  };

  static extractCustomConfig(element, def, dataset) {
    return {
      min: this.parseNumeric('min', element, def, dataset),
      max: this.parseNumeric('max', element, def, dataset),
      step: this.parseNumeric('step', element, def, dataset),
      precision: dataset.precision !== undefined ? parseInt(dataset.precision) : def.precision,
      decimalSeparator: dataset.decimalSeparator || def.decimalSeparator,
      groupingSeparator: dataset.groupingSeparator || def.groupingSeparator,
      allowNegative: dataset.allowNegative === 'true' || def.allowNegative
    };
  }

  static setupElement(instance) {
    const {element, config} = instance;

    instance.initValue = (value) => {
      return NumberElementFactory.formatNumber(value, config);
    };

    instance.parseNumber = function(value) {
      return NumberElementFactory.parseNumber(value, config);
    };

    instance.formatNumber = function(value) {
      return NumberElementFactory.formatNumber(value, config);
    };

    instance.validateValue = (value, valueChange) => {
      const {formatted, error} = this.formatValue(value, element, config);
      return {validatedValue: formatted, error};
    };

    config.formatter = (value) => {
      return this.formatValue(value, element, config);
    };

    return instance;
  }

  static setupEventListeners(instance) {
    const {element, config} = instance;

    const debouncedValidate = Utils.function.debounce(() => instance.validate(undefined, false), 300);

    const handlers = {
      input: () => {
        instance.state.modified = true;
        debouncedValidate();
      },

      change: () => {
        this.applyFormatting(instance);
      },

      blur: () => {
        this.applyFormatting(instance);
      },

      focus: () => {
        FormError.clearFieldError(element.id);

        const state = ElementFactory._privateState.get(element);
        if (state) {
          state.formatting = true;

          const parsedValue = instance.parseNumber(element.value);
          if (!isNaN(parsedValue)) {
            element.value = parsedValue.toString().replace('.', config.decimalSeparator);
          }

          state.formatting = false;
        }
      },

      keydown: (e) => {
        if (this.handleSpecialKeys(e, instance)) {
          return;
        }

        if (!this.isAllowedKey(e, config)) {
          e.preventDefault();
          return false;
        }
      },

      paste: (e) => {
        if (!this.handlePaste(e, instance)) {
          e.preventDefault();
          return false;
        }
      }
    };

    Object.entries(handlers).forEach(([event, handler]) => {
      EventSystemManager.addHandler(element, event, handler);
    });

    return handlers;
  }

  static applyFormatting(instance) {
    const {element, config} = instance;
    const value = element.value;

    if (value === '') return;

    const parsedValue = instance.parseNumber(value);
    if (isNaN(parsedValue)) return;

    const formatted = this.formatNumber(parsedValue, config);
    element.value = formatted;
  }

  static handleSpecialKeys(e, instance) {
    const {element, config} = instance;

    if ([
      'Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight', 'Home', 'End',
      'Delete', 'Backspace'
    ].includes(e.key)) {
      return true;
    }

    if (e.ctrlKey && ['a', 'c', 'v', 'x', 'z'].includes(e.key.toLowerCase())) {
      return true;
    }

    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();

      const currentValue = instance.parseNumber(element.value) || 0;
      const step = parseFloat(config.step) || 1;

      let newValue;
      if (e.key === 'ArrowUp') {
        newValue = Utils.number.round(currentValue + step, config.precision);
      } else {
        newValue = Utils.number.round(currentValue - step, config.precision);
      }

      if (config.min !== null && newValue < config.min) {
        newValue = config.min;
      }
      if (config.max !== null && newValue > config.max) {
        newValue = config.max;
      }

      const state = ElementFactory._privateState.get(element);
      if (state) {
        state.formatting = true;
        element.value = String(newValue).replace('.', config.decimalSeparator);
        state.formatting = false;
      }

      return true;
    }

    return false;
  }

  static isAllowedKey(e, config) {
    const key = e.key;

    if (/^\d$/.test(key)) {
      return true;
    }

    if (key === config.decimalSeparator &&
      e.target.value.indexOf(config.decimalSeparator) === -1 &&
      config.precision !== 0) {
      return true;
    }

    if (key === '-' && config.allowNegative && e.target.selectionStart === 0 &&
      e.target.value.indexOf('-') === -1) {
      return true;
    }

    return false;
  }

  static handlePaste(e, instance) {
    const {element, config} = instance;

    const clipboardData = e.clipboardData || window.clipboardData;
    const pastedText = clipboardData.getData('text');

    const currentValue = element.value;
    const selectionStart = element.selectionStart;
    const selectionEnd = element.selectionEnd;

    const newValue = currentValue.substring(0, selectionStart) +
      pastedText +
      currentValue.substring(selectionEnd);

    const decimalSeparatorCount = (newValue.match(new RegExp(`\\${config.decimalSeparator}`, 'g')) || []).length;
    if (decimalSeparatorCount > 1 || (config.precision === 0 && decimalSeparatorCount > 0)) {
      return false;
    }

    if (config.allowNegative) {
      const minusCount = (newValue.match(/-/g) || []).length;
      if (minusCount > 1 || (minusCount === 1 && newValue.indexOf('-') !== 0)) {
        return false;
      }
    } else if (newValue.includes('-')) {
      return false;
    }

    const validPattern = config.allowNegative ?
      new RegExp(`^-?\\d*\\${config.decimalSeparator}?\\d*$`) :
      new RegExp(`^\\d*\\${config.decimalSeparator}?\\d*$`);

    if (!validPattern.test(newValue)) {
      return false;
    }

    return true;
  }

  static formatNumber(value, config) {
    if (value === '' || value === null || value === undefined) {
      return '';
    }

    let number = typeof value === 'number' ? value : this.parseNumber(value, config);
    if (isNaN(number)) {
      return typeof value === 'string' ? value : '';
    }

    const isNegative = number < 0;
    number = Math.abs(number);

    if (config.roundValues && config.precision >= 0) {
      number = Utils.number.round(number, config.precision);
    }

    let numStr = config.roundValues ? number.toFixed(config.precision) : number.toString();
    let [intPart, decPart = ''] = numStr.split('.');

    if (!config.roundValues && config.precision > 0 && decPart) {
      decPart = decPart.substring(0, config.precision);
    } else if (config.precision === 0) {
      decPart = '';
    }

    if (config.padToPrecision && config.precision > 0 && decPart) {
      decPart = decPart.padEnd(config.precision, '0');
    }

    let formattedIntPart = intPart;
    if (config.useGrouping) {
      let grouped = '';
      for (let i = 0; i < formattedIntPart.length; i++) {
        if (i > 0 && (formattedIntPart.length - i) % 3 === 0) {
          grouped += config.groupingSeparator;
        }
        grouped += formattedIntPart[i];
      }
      formattedIntPart = grouped;
    }

    let formatted = formattedIntPart;
    if (decPart && config.precision > 0) {
      formatted += config.decimalSeparator + decPart;
    }
    if (config.showSymbol && config.symbol) {
      formatted = config.symbolPosition === 'before' ? `${config.symbol}${formatted}` : `${formatted}${config.symbol}`;
    }

    if (isNegative) {
      formatted = config.negativeWithParentheses ? `(${formatted})` : `-${formatted}`;
    }

    return formatted;
  }

  static formatValue(value, element, config) {
    let error = null;

    if (value === '' || value === null || value === undefined) {
      if (element.hasAttribute('required')) {
        error = Now.translate('Please fill in');
      }
      return {formatted: '', error};
    }

    let number = this.parseNumber(value, config);
    if (isNaN(number)) {
      return {formatted: value, error: Now.translate('Please enter a valid number')};
    }

    if (!config.allowNegative && number < 0) {
      error = Now.translate('Unable to have negative values');
      number = Math.abs(number);
    }

    const min = parseFloat(element.min);
    const max = parseFloat(element.max);

    if (min !== null && number < min) {
      error = Now.translate('Value must be at least {min}', {min});
      number = min;
    }
    if (max !== null && number > max) {
      error = Now.translate('Value must be no more than {max}', {max});
      number = max;
    }


    const formatted = this.formatNumber(number, config);
    return {formatted, error};
  }

  static parseNumber(value, config) {
    if (typeof value === 'number') {
      return isNaN(value) ? NaN : value;
    }
    if (value === '' || value === null || value === undefined) return NaN;

    let strValue = String(value);
    strValue = strValue.replace(config.decimalSeparator, '.')
      .replace(/[^0-9\.\-\(\)]+/g, '');

    if (strValue.startsWith('(') && strValue.endsWith(')')) {
      strValue = '-' + strValue.substring(1, strValue.length - 1);
    }

    return Number(strValue);
  }
}

class CurrencyElementFactory extends NumberElementFactory {
  static config = {
    ...NumberElementFactory.config,
    precision: 2,                // 2 decimal places for currency
    padToPrecision: true,        // Always show 2 decimal places
    roundValues: true,           // Round to 2 decimal places
    showSymbol: true,            // Show currency symbol
    symbol: '$',                 // Default currency symbol
    symbolPosition: 'before',    // Symbol before number
    allowNegative: true,         // Allow negative values
    useGrouping: true,           // Use thousand separators
    groupingSeparator: ',',      // Thousand separator
    decimalSeparator: '.',       // Decimal separator
    negativeWithParentheses: false, // Don't use parentheses for negative
    inputMode: 'decimal'         // Use decimal keyboard on mobile
  };
}

ElementManager.registerElement('number', NumberElementFactory);
ElementManager.registerElement('currency', CurrencyElementFactory);
